import { Link } from "react-router-dom";
import type { Topic } from "@/types/content";
import { formatNumber, formatRelative } from "@/utils/format";

interface TopicCardProps {
  topic: Topic;
  rank?: number;
}

export function TopicCard({ topic, rank }: TopicCardProps) {
  return (
    <article className="group flex h-full flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-300">
      <Link
        to={`/topics/${topic.id}`}
        className="relative block h-44 w-full overflow-hidden bg-background-200 md:h-48"
        aria-label={topic.title}
      >
        <img
          src={topic.cover}
          alt={`${topic.title} 话题封面`}
          title={`${topic.title} - 爆料看点`}
          className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
        <span className="absolute left-3 top-3 inline-flex items-center gap-1 rounded-full bg-primary-500 px-2.5 py-1 font-label text-xs text-background-50">
          <span className="flex h-3.5 w-3.5 items-center justify-center">
            <i className="ri-fire-line text-xs" aria-hidden="true" />
          </span>
          {topic.category}
        </span>
        {typeof rank === "number" && (
          <span className="absolute right-3 top-3 flex h-7 w-7 items-center justify-center rounded-md bg-foreground-950/80 font-heading text-sm font-bold text-background-50">
            {rank}
          </span>
        )}
      </Link>
      <div className="flex flex-1 flex-col p-4 md:p-5">
        <h3 className="font-heading text-base font-bold leading-snug text-foreground-950 md:text-lg">
          <Link to={`/topics/${topic.id}`} className="cursor-pointer hover:text-primary-600 transition-colors">
            {topic.title}
          </Link>
        </h3>
        <p className="mt-2 line-clamp-3 text-sm leading-relaxed text-foreground-600">
          {topic.summary}
        </p>
        <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-background-200 pt-3 font-label text-xs text-foreground-500">
          <span className="inline-flex items-center gap-1">
            <span className="flex h-3.5 w-3.5 items-center justify-center">
              <i className="ri-fire-line" aria-hidden="true" />
            </span>
            {formatNumber(topic.heat)} 热度
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="flex h-3.5 w-3.5 items-center justify-center">
              <i className="ri-chat-3-line" aria-hidden="true" />
            </span>
            {formatNumber(topic.commentCount)} 条讨论
          </span>
          <span className="ml-auto whitespace-nowrap">{formatRelative(topic.date)}</span>
        </div>
      </div>
    </article>
  );
}

export function TopicRow({ topic, rank }: TopicCardProps) {
  return (
    <article className="group flex gap-3 border-b border-background-200 py-3 last:border-b-0 md:gap-4">
      {typeof rank === "number" && (
        <span
          className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-md font-heading text-sm font-bold ${
            rank <= 3 ? "bg-primary-500 text-background-50" : "bg-background-200 text-foreground-600"
          }`}
        >
          {rank}
        </span>
      )}
      <div className="min-w-0 flex-1">
        <h3 className="font-heading text-sm font-bold leading-snug text-foreground-950 md:text-base">
          <Link to={`/topics/${topic.id}`} className="cursor-pointer transition-colors hover:text-primary-600">
            {topic.title}
          </Link>
        </h3>
        <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 font-label text-xs text-foreground-500">
          <span className="text-secondary-700">{topic.category}</span>
          <span className="inline-flex items-center gap-1">
            <span className="flex h-3.5 w-3.5 items-center justify-center">
              <i className="ri-fire-line" aria-hidden="true" />
            </span>
            {formatNumber(topic.heat)}
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="flex h-3.5 w-3.5 items-center justify-center">
              <i className="ri-chat-3-line" aria-hidden="true" />
            </span>
            {formatNumber(topic.commentCount)}
          </span>
        </div>
      </div>
      <Link
        to={`/topics/${topic.id}`}
        className="hidden h-16 w-24 shrink-0 overflow-hidden rounded-md bg-background-200 sm:block"
        aria-label={topic.title}
      >
        <img
          src={topic.cover}
          alt={`${topic.title} 话题封面`}
          title={`${topic.title} - 爆料看点`}
          className="h-full w-full object-cover object-top"
        />
      </Link>
    </article>
  );
}